import { memo } from "react";
import { Box } from "@mui/material";

import Txt from "../../../../../components/typography/Txt";
import { useIsAcquisitions } from "../../../../../pipelines/PipelineContext";

import {
  PALETTE,
  Panel,
  DateRow,
  fmtDate,
  fmtRelativeShort,
  daysFromNow,
} from "./_shared";

/**
 * Loan-level key dates only. Borrower and property dates live in their own
 * cards so this list stays focused on the deal's lifecycle markers.
 */
const LoanDatesCard = ({ loan, leadCreatedDate, latestMilestoneChange }) => {
  // An acquisition settles a trade rather than closing a loan: its target is
  // the expected settlement date (the seller's expected closing stays on Loan Info).
  const isAcquisition = useIsAcquisitions();
  const targetDate = isAcquisition
    ? loan?.expectedSettlementDate
    : loan?.expectedClosingDate;
  const noun = isAcquisition ? "settlement" : "closing";
  const closingDays = daysFromNow(targetDate);
  const closingSoon =
    closingDays != null && closingDays >= 0 && closingDays <= 14;
  const closingOverdue = closingDays != null && closingDays < 0;

  const rows = [
    leadCreatedDate && {
      key: "lead",
      when: fmtDate(leadCreatedDate),
      whenSub: fmtRelativeShort(leadCreatedDate),
      what: "Lead created",
      whatSub: "First borrower sign-up",
      badgeLabel: "Lead",
      badgeTone: "default",
      state: "default",
    },

    latestMilestoneChange?.milestoneChangeDate && {
      key: "milestone",
      when: fmtDate(latestMilestoneChange.milestoneChangeDate),
      whenSub: fmtRelativeShort(latestMilestoneChange.milestoneChangeDate),
      what: latestMilestoneChange.updatedMilestoneStatus || "Stage updated",
      whatSub: latestMilestoneChange.priorMilestoneStatus
        ? `from ${latestMilestoneChange.priorMilestoneStatus}`
        : null,
      badgeLabel: "Stage",
      badgeTone: "default",
      state: "default",
    },
    targetDate && {
      key: "closing",
      when: fmtDate(targetDate),
      whenSub: fmtRelativeShort(targetDate),
      what: `Expected ${noun} date`,
      whatSub: closingOverdue
        ? `Past expected ${noun}`
        : closingSoon
          ? isAcquisition
            ? "Settlement window"
            : "Closing window"
          : null,
      badgeLabel: isAcquisition ? "Settlement" : "Closing",
      badgeTone: closingOverdue || closingSoon ? "urgent" : "default",
      state: closingOverdue || closingSoon ? "urgent" : "default",
    },
    loan?.firstPaymentDate && {
      key: "firstPayment",
      when: fmtDate(loan.firstPaymentDate),
      whenSub: fmtRelativeShort(loan.firstPaymentDate),
      what: "First payment",
      whatSub: null,
      badgeLabel: "Payment",
      badgeTone: "default",
      state: "default",
    },
    loan?.maturityDate && {
      key: "maturity",
      when: fmtDate(loan.maturityDate),
      whenSub: fmtRelativeShort(loan.maturityDate),
      what: "Maturity date",
      whatSub: null,
      badgeLabel: "Maturity",
      badgeTone: "default",
      state: "default",
    },
  ].filter(Boolean);

  return (
    <Panel title="Loan dates" count={`${rows.length}`}>
      {rows.length === 0 ? (
        <Box sx={{ px: "22px", py: "20px" }}>
          <Txt
            sx={{
              fontSize: 12.5,
              color: PALETTE.ink3,
              fontStyle: "italic",
            }}
          >
            No loan-level dates tracked yet.
          </Txt>
        </Box>
      ) : (
        rows.map((row, idx) => (
          <DateRow key={row.key} isFirst={idx === 0} {...row} />
        ))
      )}
    </Panel>
  );
};

export default memo(LoanDatesCard);
